/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import * as React from 'react';

import { IDobbiesCheckoutOrderSummaryData } from './dobbies-checkout-order-summary.data';
import { IDobbiesCheckoutOrderSummaryProps } from './dobbies-checkout-order-summary.props.autogenerated';
import { getLineItems } from './components/get-checkout-summary-line-items';
import { ArrayExtensions, QueryResultSettingsProxy } from '@msdyn365-commerce-modules/retail-actions';
import { getByIdsAsync } from '@msdyn365-commerce/retail-proxy/dist/DataActions/ProductsDataActions.g';
import { getCheckoutState } from '@msdyn365-commerce/global-state';
import { IImageSettings } from '@msdyn365-commerce/core';
import { ILineItems } from '@msdyn365-commerce-modules/checkout';
import { CheckoutAccordionStateInput } from '../../themes/dobbies/actions/checkout-accordion-steps-state';
import { _getScreenSize } from '../../themes/dobbies/utilities/display-utils';
import { reaction } from 'mobx';

export interface IDobbiesCheckoutOrderSummaryViewProps extends IDobbiesCheckoutOrderSummaryProps<IDobbiesCheckoutOrderSummaryData> {
    lineItems?: ILineItems;
    onSave(): void;
}

export interface IDobbiesCheckoutOrderSummaryState {
    lineItems?: ILineItems;
    cartHasHomeDelItems?: boolean;
    zipCodeUpdated?: boolean;
}

/**
 *
 * DobbiesCheckoutOrderSummary component
 * @extends {React.PureComponent<IDobbiesCheckoutOrderSummaryProps<IDobbiesCheckoutOrderSummaryData>>}
 */
class DobbiesCheckoutOrderSummary extends React.PureComponent<
    IDobbiesCheckoutOrderSummaryProps<IDobbiesCheckoutOrderSummaryData>,
    IDobbiesCheckoutOrderSummaryState
> {
    public constructor(props: IDobbiesCheckoutOrderSummaryProps<IDobbiesCheckoutOrderSummaryData>) {
        super(props);
        this.state = {};
    }
    public async componentDidMount(): Promise<void> {
        const checkoutState = await getCheckoutState(this.props.context.actionContext);
        const cartProductIds = checkoutState.checkoutCart.cart.CartLines?.map(cartline => cartline.ProductId);
        checkoutState.checkoutCart.cart.CartLines?.map(cl => {
            if (!cl.FulfillmentStoreId) {
                this.setState({ cartHasHomeDelItems: true });
            }
        });
        const validProductIds: number[] = [];
        cartProductIds?.map(cprodId => {
            if (cprodId) {
                validProductIds.push(cprodId);
            }
        });
        const simpleProductsData = ArrayExtensions.validValues(
            await getByIdsAsync(
                {
                    callerContext: this.props.context.actionContext,
                    queryResultSettings: QueryResultSettingsProxy.getPagingFromInputDataOrDefaultValue(this.props.context.actionContext)
                },
                this.props.context.actionContext.requestContext.apiSettings.channelId
                    ? this.props.context.actionContext.requestContext.apiSettings.channelId
                    : -1,
                validProductIds,
                null,
                this.props.context.actionContext.requestContext.apiSettings.catalogId
            )
        );
        const defaultImageSettings: IImageSettings = {
            viewports: {
                xs: { q: 'w=275&h=275&m=6', w: 275, h: 275 },
                lg: { q: 'w=275&h=275&m=6', w: 275, h: 275 },
                xl: { q: 'w=275&h=275&m=6', w: 275, h: 275 }
            },
            lazyload: true
        };
        const lineItems = await getLineItems(
            this.props.context,
            simpleProductsData,
            this.props.data.catalogs?.result || [],
            '',
            this.props.data.channelDeliveryOptionConfig.result,
            undefined,
            this.props.data.featureState.result,
            this.props.data.deliveryOptions.result,
            defaultImageSettings
        );
        this.setState({ lineItems: lineItems });
        const screenSize = _getScreenSize(this.props.context);
        if (screenSize === 3 || screenSize === 4) {
            this.props.context.actionContext.update(new CheckoutAccordionStateInput(), { currentStep: 0 });
        } else {
            if (checkoutState.checkoutCart.cart.CartLines?.find(cl => !cl.FulfillmentStoreId)) {
                this.props.context.actionContext.update(new CheckoutAccordionStateInput(), { currentStep: 1 });
            } else {
                this.props.context.actionContext.update(new CheckoutAccordionStateInput(), { currentStep: 2 });
            }
        }
        reaction(
            () => this.props.data.checkout.result?.shippingAddress?.ZipCode,
            () => {
                this.setState({ zipCodeUpdated: true });
            }
        );
    }

    private onSave = () => {
        if (!this.state.cartHasHomeDelItems) {
            this.props.context.actionContext.update(new CheckoutAccordionStateInput(), { currentStep: 2 });
            return;
        }
        this.props.context.actionContext.update(new CheckoutAccordionStateInput(), { currentStep: 1 });
    };

    public render(): JSX.Element | null {
        if (this.state.zipCodeUpdated) {
            this.setState({ zipCodeUpdated: false });
        }
        const viewProps: IDobbiesCheckoutOrderSummaryViewProps = {
            ...this.props,
            lineItems: this.state.lineItems,
            onSave: this.onSave
        };
        return this.props.renderView(viewProps);
    }
}

export default DobbiesCheckoutOrderSummary;
